/**
 * AlertsByDateChart — daily alert volume for the selected window.
 *
 * Server pre-fills empty days via generate_series so the chart never has
 * gaps. We pick a bar chart (not a line) because "zero alerts on Monday"
 * is meaningful, and bars communicate that better than a flat segment.
 */
import { useMemo } from 'react';
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import type { DailyBucket } from '../../services/reportsApi';

interface Props {
  data: DailyBucket[];
}

export default function AlertsByDateChart({ data }: Props) {
  const rows = useMemo(
    () =>
      data.map((d) => ({
        // Parse the YYYY-MM-DD as-local so the tooltip shows the same day
        // the server bucketed. We don't care about exact timezone alignment
        // for a daily bucket.
        dateIso: d.date,
        date: parseISO(d.date),
        count: d.count,
      })),
    [data],
  );

  const total = rows.reduce((acc, r) => acc + r.count, 0);

  // Choose tick format based on how many days we're showing — daily labels
  // get crowded past ~30 bars.
  const tickFormat = rows.length > 60 ? 'MMM' : rows.length > 30 ? 'MMM d' : 'MM/dd';

  return (
    <div className="glass-card p-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-sm font-semibold text-white">Detections by Date</h3>
          <p className="text-xs text-gray-500">
            {total.toLocaleString()} detections across {rows.length} days
          </p>
        </div>
      </div>

      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={rows} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#2c4567" />
            <XAxis
              dataKey="date"
              tickFormatter={(d: Date) => format(d, tickFormat)}
              stroke="#6b7d97"
              tick={{ fill: '#9aa7be', fontSize: 11, fontFamily: 'JetBrains Mono, monospace' }}
              minTickGap={18}
            />
            <YAxis
              stroke="#6b7d97"
              tick={{ fill: '#9aa7be', fontSize: 11, fontFamily: 'JetBrains Mono, monospace' }}
              allowDecimals={false}
            />
            <Tooltip
              cursor={{ fill: 'rgba(255, 181, 71, 0.08)' }}
              contentStyle={{
                background: '#142a4a',
                border: '1px solid #2c4567',
                borderRadius: 2,
                fontSize: 12,
                color: '#e9edf2',
                fontFamily: 'JetBrains Mono, monospace',
              }}
              labelFormatter={(d: Date) => format(d, 'EEE, MMM d yyyy')}
              formatter={(v: number) => [v.toLocaleString(), 'Alerts']}
            />
            <Bar dataKey="count" fill="#ffb547" radius={[1, 1, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
